import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentBox } from '@pokemon/design.ui.containers.content-box'
import { AuthContextValue, AuthContext } from '../../utils/auth/context'
import { Typography } from '@pokemon/design-v2.typography'
import { Button } from '@pokemon/design-v2.button'
import { TextField, TextFieldProps } from '@pokemon/design-v2.text-field'
import { TextFieldProps as MUITextFieldProps } from '@mui/material/TextField'
import { Form, Field, FieldProps, FieldRenderProps } from 'react-final-form'
import { required } from '../verify/validation'
import { ChangeProfilePayload, changeProfile } from '../../utils/tamApi'
import { useNavigate } from 'react-router'
import { Alert } from '@pokemon/design-v2.alert'
import { Box } from '@pokemon/design-v2.box'
import { Stack } from '@pokemon/design-v2.stack'
import { PlayExitModal } from './play_exit_modal'

export interface PlayNamePageProps {
  readonly setError: (text?: string) => void,
}

export function PlayNamePage (props: PlayNamePageProps) {
  const { loggedUserProfile, isChildView, accessToken, selectedAccount } = useContext<AuthContextValue>(AuthContext)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const onSubmit = async (values: ChangeProfilePayload) => {
    try {
      if (Object.keys(values).length > 0) {
        await changeProfile(accessToken!.token, values, isChildView ? selectedAccount!.guid : undefined)
      }
      navigate('/play-pokemon') // change to next page in flow when implemented
    } catch (err) {
      props.setError()
    }
  }

  return (
    <ContentBox
      autoSizeBox={true}
      stepIndicatorProps={{ steps: 4, currentStep: 0 }}
      onClose={() => setIsModalOpen(true)}
    >
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" component="h1" data-testid="play-name-title">
                  {t('first_last_name_page_title', 'Enter Your First and Last Name')}
                </Typography>
                <Typography variant='body1' data-testid="play-name-description">
                  {t('first_and_last_name_disclosure', "Your first and last name may be shown publicly in places such as the Pokemon.com website, on match slips and pairings during tournaments, on Pokémon's official streams, etc.")}
                </Typography>
              </Box>

              <Stack spacing={2}>
                {loggedUserProfile?.first_name && loggedUserProfile?.last_name &&
                  <Alert data-testid="play-name-already-set" variant="outlined" severity="success">{t('first_and_last_name_already_set', 'Your First Name and Last Name are already set!')}</Alert>
                }

                <PlayNameTextField
                  value={loggedUserProfile?.first_name}
                  fieldProps={{
                    name: 'first_name',
                    validate: required(t('verification.veratad.form.errors.first_name.required', 'First name is required.'))
                  }}
                  textFieldProps={{
                    id: 'play-name-first',
                    label: t('placeholder_first_name', 'First Name')
                  }}
                />

                <PlayNameTextField
                  value={loggedUserProfile?.last_name}
                  fieldProps={{
                    name: 'last_name',
                    validate: required(t('verification.veratad.form.errors.last_name.required', 'Last name is required.'))
                  }}
                  textFieldProps={{
                    id: 'play-name-last',
                    label: t('placeholder_last_name', 'Last Name')
                  }}
                />
              </Stack>

              <Button type="submit" disabled={submitting} disableRipple data-testid="play-name-next" text={t('next_button_label', 'NEXT')}/>
            </Stack>
          </form>
        )}
      />
      <PlayExitModal isModalOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} />
    </ContentBox>
  )
}

interface PlayNameTextFieldProps {
  readonly value?: string
  readonly fieldProps: FieldProps<string, FieldRenderProps<string, HTMLElement, string>, HTMLElement, string>
  readonly textFieldProps: TextFieldProps & MUITextFieldProps
}

function PlayNameTextField (props: PlayNameTextFieldProps) {
  if (props.value) {
    return (
      <TextField {...props.textFieldProps} value={props.value} disabled />
    )
  }

  return (
    <Field {...props.fieldProps}>
      {({ input, meta }) => (
        <TextField
          {...input}
          {...props.textFieldProps}
          error={meta.touched && meta.error}
          helperText={meta.touched && meta.error}
        />
      )}
    </Field>
  )
}
